import { cva } from 'class-variance-authority';
import { cn } from './utils';

import type { VariantProps } from 'class-variance-authority';

export const buttonVariants = cva(
  [
    'group inline-flex items-center justify-center',
    'w-fit h-fit select-none',
    'whitespace-nowrap',
    'transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus',
    'disabled:cursor-default',
  ],
  {
    variants: {
      variant: {
        default: 'bg-button-primary hover:bg-button-primary-hover disabled:bg-button-disabled',
        secondary: 'bg-button-secondary hover:bg-button-secondary-hover disabled:bg-button-disabled text-text-primary',
        danger: 'bg-button-danger hover:bg-button-danger-hover disabled:bg-button-disabled',
        destructive: 'bg-button-danger hover:bg-button-danger-hover disabled:bg-button-disabled',
        outline: 'bg-button-outline hover:bg-button-outline-hover ring-[1px] ring-border ring-inset',
        inverse: 'bg-button-inverse hover:bg-button-inverse-hover disabled:bg-button-disabled',
        ghost: 'bg-transparent hover:bg-button-secondary-hover',
        link: 'bg-transparent hover:bg-transparent disabled:bg-transparent',
        'link-secondary': 'bg-transparent hover:bg-transparent disabled:bg-transparent',
        cta: 'bg-teal-600 hover:bg-teal-500 disabled:bg-button-disabled',
      },
      size: {
        default: 'py-3 px-4 text-base rounded-md',
        medium: 'py-3 px-4 text-base rounded-md',
        sm: 'py-1.5 px-3 text-sm rounded-sm',
        small: 'py-1.5 px-3 text-sm rounded-sm',
        xs: 'min-h-auto py-1 px-2 text-sm rounded-sm',
        'extra-small': 'min-h-auto py-1 px-2 text-sm rounded-sm',
        lg: 'py-2.5 px-8 text-sm rounded-md',
        icon: 'p-2 rounded-md',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
);

export type ButtonVariantProps = VariantProps<typeof buttonVariants>;

type GapSize = 1 | 2 | 3 | 4;

export const gapClasses: { [K in GapSize]: string } = {
  1: 'gap-1',
  2: 'gap-2',
  3: 'gap-3',
  4: 'gap-4',
};

/**
 * Gets text color class name based on variant/size/disabled state.
 * Separated from the component for testability and clarity.
 */
export function getTextClassName(variant: ButtonVariantProps['variant'], size: ButtonVariantProps['size'], isDisabled: boolean): string {
  if (isDisabled) return 'text-text-disabled';

  const isSmallSize = size === 'small' || size === 'extra-small' || size === 'sm' || size === 'xs';
  const leading = isSmallSize ? 'leading-[20px]' : 'leading-[24px]';

  const linkBase = cn('underline underline-offset-2 font-normal', leading);

  switch (variant) {
    case 'default':
      return cn('text-text-brand group-hover:text-text-brand', leading);
    case 'secondary':
      return cn('text-text-primary group-hover:text-text-primary', leading);
    case 'inverse':
      return cn('text-text-inverse group-hover:text-text-inverse', leading);
    case 'ghost':
      return cn('text-text-primary group-hover:text-text-primary', leading);
    case 'outline':
      return cn('text-text-primary group-hover:text-text-primary', leading);
    case 'danger':
    case 'destructive':
      return cn('text-text-error group-hover:text-text-error', leading);
    case 'cta':
      return cn('text-text-primary group-hover:text-text-primary', leading);
    case 'link':
      return cn(linkBase, 'text-link-primary group-hover:text-link-primary-hover');
    case 'link-secondary':
      return cn(linkBase, 'text-link-secondary group-hover:text-link-secondary-hover');
    default:
      return cn('text-text-primary group-hover:text-text-primary', leading);
  }
}
